<template lang="pug">
div(style="max-width: 100%; overflow-x: hidden; background-color: var(--dark-blue-70) !important; height: 100%; display: flex; flex-direction: column;")
    v-overlay(
        v-if="isInitialLoading"
        :value="isInitialLoading"
        :absolute="true"
        :opacity="0.8"
    )
        v-progress-circular(
            indeterminate
            size="64"
            color="primary"
        )
    BigBoardTable(
        :calls-waiting="callsWaiting"
        :cookie-settings-object="cookieSettingsObject"
        :base-dashboard-summary-by-summary-id="existingBaseDashboardSummaryBySummaryId"
        :dashboard-summary-objects="dashboardSummaryObjects"
        :display-view="displayView"
        :rules="rules"
        :scope-parameters="scopeParameters"
        :total-active-agents="totalActiveAgentCount"
    )
    template(v-if="cookieSettingsObject.showUserCardSection")
        AgentCardSection(
            :agents="agents"
            :agents-loading="agentsLoading"
            :cookie-settings-object="cookieSettingsObject"
            :dashboard-summary-objects="dashboardSummaryObjects"
            :rules="rules"
            :scope-parameters="scopeParameters"
        )
</template>

<script lang="ts">
import Vue from 'vue';
import VolieState from "@/store";

// Helpers
import { Mutex } from '@/helpers/mutex';
import { throttle } from '@/helpers/coroutine';
import { defer } from '@/helpers/defer';

// Components
import AgentCardSection from '@/components/dashboards/big_board/agent_card_section.vue';
import BigBoardTable from '@/components/dashboards/big_board/table.vue';

export default Vue.extend({
    data() {
        return {
            agentsViewClockInterval: null,
            dashboardSummaryObjects: [],
            buildRenderObjectsMutex: new Mutex(),
            buildRenderObjectsThrottle: null,
            existingBaseDashboardSummaryBySummaryId: new Map<number, any>(),
            existingDashboardSummaryPeriodBySummaryId: new Map<number, any>(),
            isInitialLoading: true,
            lastAgentsWatchSnapshotKey: '',
        }
    },
    methods: {
        async buildRenderAgents() {
            if (!(this.agents?.length > 0)) {
                return;
            }
            
            // ensure data is built before filtering
            if (!(await this.getBuildData())) {
                return;
            }

            for (let i = 0; i < this.agents.length; i++) {
                const agent = this.agents[i];
                if (!agent || !(agent.account_user_id > 0)) {
                    continue;
                }

                if (!(await this.hasValidSummaryType(agent))) {
                    continue;
                }

                this.dashboardSummaryObjects.push({
                    appointments_created_today:     agent.appointments_created_today,
                    created_at:                     agent.agent_created_at,
                    display_title:                  agent.user_full_name,
                    inbound_calls_today:            agent.inbound_calls_today,
                    outbound_calls_today:           agent.outbound_calls_today,
                    agent_status:                   agent.agent_status_name,
                    agent_status_type:              agent.agent_status_type,
                    is_online:                      agent.is_online,
                    time_in_status:                 agent.time_in_seconds,
                    active_conversations:           agent.total_assigned_return_message_campaign_customers,
                    unresponded_to_time_in_seconds: agent.unresponded_to_time_in_seconds,
                });
            }
        },
        async buildRenderObjects(clear: boolean = false) {
            if (clear) {
                this.dashboardSummaryObjects = [];
                this.existingDashboardSummaryPeriodBySummaryId.clear();
                this.existingBaseDashboardSummaryBySummaryId.clear();
            }

            // ensures data is built before constructing the data array
            // this allows us to process the base dashboard summaries concurrently
            if (!(await this.getBuildData())) {
                return;
            }

            if (this.currentTab === "agents-view") {
                this.dashboardSummaryObjects = [];
                await this.buildRenderAgents();
                this.isInitialLoading = false;
                return;
            }

            const localSummaryIds = new Map<number, boolean>();
            async function process(baseDashboardSummary: any) {
                if (!baseDashboardSummary || !(baseDashboardSummary?.base_dashboard_summary?.id > 0) || !(await this.hasValidSummaryType(baseDashboardSummary?.base_dashboard_summary))) {
                    return;
                }

                localSummaryIds.set(baseDashboardSummary?.data?.dashboard_summary?.id, true);

                // don't process if already exists
                if (this.existingDashboardSummaryPeriodBySummaryId.has(baseDashboardSummary?.data?.dashboard_summary?.id)) {
                    // update the existing base dashboard summary
                    this.copyBaseDashboardSummaryValues(baseDashboardSummary);
                    return;
                }

                // find data's day period
                const sourceDayPeriod = baseDashboardSummary?.data?.dashboard_summary_periods?.find(i => i.period_type === "day");
                if (!sourceDayPeriod || !(sourceDayPeriod.id > 0)) {
                    return;
                }

                // create a new in-memory instance (shallow copy)
                const dayPeriod = { ...sourceDayPeriod };

                // add organization/team name
                dayPeriod.display_title = await this.getBaseDashboardSummaryTitle(baseDashboardSummary);

                // if invalid, return
                if (!dayPeriod.display_title) {
                    return;
                }

                // add to objects
                this.dashboardSummaryObjects.push(dayPeriod);
                this.existingDashboardSummaryPeriodBySummaryId.set(baseDashboardSummary?.data?.dashboard_summary?.id, dayPeriod);
                this.existingBaseDashboardSummaryBySummaryId.set(baseDashboardSummary?.data?.dashboard_summary?.id, baseDashboardSummary);
            }
            
            // Loop over base dashboard summaries concurrently
            await Promise.all(this.baseDashboardSummaries.map(item => process.call(this, item)));

            // remove any summaries that are no longer in the summary objects
            // filter is used for O(n) operation
            this.dashboardSummaryObjects = this.dashboardSummaryObjects.filter(item => {
                const exists = localSummaryIds.has(item.dashboard_summary_id);

                if (!exists) {
                    this.existingDashboardSummaryPeriodBySummaryId.delete(item.dashboard_summary_id);
                    this.existingBaseDashboardSummaryBySummaryId.delete(item.dashboard_summary_id);
                }

                return exists;
            });

            this.setActiveAgentCounts();
            
            // sort by name
            this.dashboardSummaryObjects.sort((a, b) => a.display_title.localeCompare(b.display_title));
            this.isInitialLoading = false;
        },
        copyBaseDashboardSummaryValues(baseDashboardSummary: any) {
            if (!baseDashboardSummary || !(baseDashboardSummary?.data?.dashboard_summary?.id > 0)) {
                return;
            }

            const sourceDayPeriod = baseDashboardSummary?.data?.dashboard_summary_periods?.find(i => i.period_type === "day");
            if (!sourceDayPeriod || !(sourceDayPeriod.id > 0)) {
                return;
            }

            const existing = this.existingDashboardSummaryPeriodBySummaryId.get(baseDashboardSummary?.data?.dashboard_summary?.id);
            if (!existing) {
                return;
            }

            // copy over values
            for (const key in existing) {
                if (sourceDayPeriod.hasOwnProperty(key)) {
                    existing[key] = sourceDayPeriod[key];
                }
            }

            existing.active_agent_count = this.calculateActiveAgents(baseDashboardSummary);
        },
        agentBelongsToTeam(agent: any, teamId: number) {
            if (!(teamId > 0)) {
                return false;
            }

            if (agent?.team_ids?.includes(teamId)) {
                return true;
            }

            const team = dataBank.r_teams?.find(t => t.team_id === teamId);
            return !!team?.user_ids?.includes(agent?.user_id);
        },
        filterAgentsForBaseDashboardSummary(baseDashboardSummary: any) {
            const agents = this.agents;
            if (!(agents?.length > 0) || !baseDashboardSummary) {
                return [];
            }

            const ownerType = baseDashboardSummary?.base_dashboard_summary?.owner_type;
            const ownerId = baseDashboardSummary?.base_dashboard_summary?.owner_id;
            const teamId = baseDashboardSummary?.base_dashboard_summary?.team_id;

            switch (ownerType) {
                case "organization": {
                    let matches = agents.filter(agent => agent.organization_id === ownerId);
                    if (teamId > 0) {
                        matches = matches.filter(agent => this.agentBelongsToTeam(agent, teamId));
                    }

                    return matches;
                }
                case "team":
                    return agents.filter(agent => this.agentBelongsToTeam(agent, ownerId));
                default:
                    return [];
            }
        },
        calculateActiveAgents(baseDashboardSummary: any) {
            return this.filterAgentsForBaseDashboardSummary(baseDashboardSummary).length;
        },
        async getBaseDashboardSummaryTitle(baseDashboardSummary: any) {
            if (baseDashboardSummary == null) {
                return '';
            }

            switch (baseDashboardSummary?.base_dashboard_summary?.owner_type) {
                case "organization":
                    const organizationUsers = await DataBank.loadResource('organization_users');
                    if (!organizationUsers || organizationUsers.length === 0) {
                        return;
                    }

                    // find organization user
                    const organizationUser = organizationUsers.find(i => i.organization_id === baseDashboardSummary?.base_dashboard_summary?.owner_id);
                    if (!organizationUser) {
                        return '';
                    }

                    return organizationUser.organization_name || '';
                case "team":
                    const teams = await DataBank.loadResource('teams');
                    if (!teams || teams.length === 0) {
                        return '';
                    }

                    // find team
                    const team = teams.find(i => i.team_id === baseDashboardSummary?.base_dashboard_summary?.owner_id);
                    if (!team) {
                        return '';
                    }

                    return team.name || '';
                default:
                    return '';
            }
        },
        async getBuildData() {
            switch (this.currentTab) {
                case "account-view":
                    await DataBank.loadResource('organization_users');
                    if (this.scopeParameters?.scopeType === "team_organization") {
                        await DataBank.loadResource('teams');
                    }
                    break;
                case "teams-view":
                    await DataBank.loadResource('teams');
                    break;
                case "agents-view":
                    if (this.scopeParameters?.scopeType === "organization") {
                        await this.getUsersInOrganization(this.scopeParameters?.scopeId);
                    } else if (this.scopeParameters?.scopeType === "team") {
                        await DataBank.loadResource('teams');
                    }
                    break;
                default:
                    return false;
            }

            return true;
        },
        // record is either a base dashboard summary or an agent
        async hasValidSummaryType(record: any) {
            switch (this.currentTab) {
                case "account-view":
                    if (record?.owner_type !== "organization") {
                        return false;
                    }

                    if (this.scopeParameters?.scopeType === "team_organization") {
                        return record?.team_id === this.scopeParameters?.scopeId;
                    }

                    return !(record?.team_id > 0);
                case "teams-view":
                    return record?.owner_type === "team";
                case "agents-view":
                    if (!(this.scopeParameters?.scopeId > 0)) {
                        return true;
                    }

                    if (this.scopeParameters?.scopeType === "organization") {
                        const organizationUsers = await this.getUsersInOrganization(this.scopeParameters?.scopeId);
                        if (!organizationUsers || organizationUsers.length === 0) {
                            return false;
                        }
                        
                        return organizationUsers.find(i => i.user_id === record?.user_id);
                    } else if (this.scopeParameters?.scopeType === "team") {
                        const team = dataBank.r_teams.find(i => i.team_id === this.scopeParameters?.scopeId);
                        if (!team) {
                            return false;
                        }

                        return team.user_ids.includes(record?.user_id);
                    }

                    return true;
                default:
                    return false;
            }
        },
        buildAgentsWatchSnapshotKey() {
            return JSON.stringify((this.agents || []).map(agent => ({
                account_user_id: agent?.account_user_id,
                agent_created_at: agent?.agent_created_at,
                agent_status_name: agent?.agent_status_name,
                agent_status_type: agent?.agent_status_type,
                appointments_created_today: agent?.appointments_created_today,
                inbound_calls_today: agent?.inbound_calls_today,
                is_online: agent?.is_online,
                organization_id: agent?.organization_id,
                outbound_calls_today: agent?.outbound_calls_today,
                total_assigned_return_message_campaign_customers: agent?.total_assigned_return_message_campaign_customers,
                user_full_name: agent?.user_full_name,
                user_id: agent?.user_id,
            })));
        },
        handleAgentsChange() {
            const snapshotKey = this.buildAgentsWatchSnapshotKey();
            if (snapshotKey === this.lastAgentsWatchSnapshotKey) {
                return;
            }
            this.lastAgentsWatchSnapshotKey = snapshotKey;

            if (this.currentTab === "agents-view") {
                this.buildRenderObjectsThrottle(true);
                return;
            }

            this.setActiveAgentCounts();
        },
        setActiveAgentCounts() {
            for (let i = 0; i < this.dashboardSummaryObjects.length; i++) {
                const obj = this.dashboardSummaryObjects[i];
                if (!obj?.dashboard_summary_id) {
                    continue;
                }

                const baseDashboardSummary = this.existingBaseDashboardSummaryBySummaryId.get(obj.dashboard_summary_id);
                obj.active_agent_count = this.calculateActiveAgents(baseDashboardSummary);
            }
        },
        startAgentsViewClock() {
            this.stopAgentsViewClock();
            this.agentsViewClockInterval = setInterval(this.tickTimeValues, 1000);
        },
        stopAgentsViewClock() {
            if (this.agentsViewClockInterval) {
                clearInterval(this.agentsViewClockInterval);
                this.agentsViewClockInterval = null;
            }
        },
        tickTimeValues() {
            if (this.displayView === 'agents-view' && this.dashboardSummaryObjects?.length) {
                for (let i = 0; i < this.dashboardSummaryObjects.length; i++) {
                    const obj = this.dashboardSummaryObjects[i];
                    if (obj) {
                        this.$set(obj, 'time_in_status', (obj.time_in_status ?? 0) + 1);
                        if (obj.unresponded_to_time_in_seconds != null) {
                            this.$set(obj, 'unresponded_to_time_in_seconds', obj.unresponded_to_time_in_seconds + 1);
                        }
                    }
                }
            }
        },
    },
    computed: {
        baseDashboardSummaries() {
            return VolieState.baseDashboardSummaries();
        },
        displayView() {
            return this.scopeParameters?.displayView || this.currentTab;
        },
        scopedAgentsForCount() {
            const agents = this.agents || [];
            if (!(agents.length > 0)) {
                return [];
            }

            if (this.scopeParameters?.scopeId > 0 && this.scopeParameters?.scopeType === "team_organization") {
                return agents.filter(agent => this.agentBelongsToTeam(agent, this.scopeParameters.scopeId));
            }

            return agents;
        },
        totalActiveAgentCount() {
            return this.scopedAgentsForCount.length;
        },
    },
    components: {
        BigBoardTable,
        AgentCardSection,
    },
    props: {
        agents: Array,
        agentsLoading: Boolean,
        callsWaiting: Array, 
        cookieSettingsObject: Object,
        currentDayDashboardSummary: Object, 
        currentBaseDashboardSummary: Object, 
        currentTab: String,
        getUsersInOrganization: Function,
        rules: Array,
        scopeParameters: Object,
    },
    watch: {
        agents: {
            handler() {
                this.handleAgentsChange();
            },
            deep: true,
        },
        baseDashboardSummaries: {
            deep: true,
            handler() {
                if (this.currentTab !== "agents-view") {
                    this.buildRenderObjectsThrottle();
                }
            }
        },
        currentTab: {
            handler() {
                this.scopeParameters.displayView = '';
                this.scopeParameters.scopeType = '';
                this.scopeParameters.scopeId = '';
            }
        },
        displayView: {
            handler() {
                this.$nextTick(() => {
                    this.buildRenderObjectsThrottle(true);
                });
            }
        },
        scopeParameters: {
            deep: true,
            handler() {
                this.buildRenderObjectsThrottle(true);
            }
        },
    },
    mounted() {
        this.buildRenderObjectsThrottle = throttle(async (clear: boolean) => {
            await defer(async defer => {
                // lock transaction to prevent concurrent access issues
                const unlock = await this.buildRenderObjectsMutex.lock();
                defer(unlock);

                // build objects
                await this.buildRenderObjects(clear);
            });
        }, 100);

        this.buildRenderObjectsThrottle();
        this.startAgentsViewClock();
    },
    destroyed() {
        this.stopAgentsViewClock();
    },
})
</script>

<style lang="scss" scoped>

</style>